import { useEffect, useState } from "react";
import useTranslator from "../hooks/useTranslator";
import { RowButton, Button, Checkbox, Icon, TextArea } from "./design";
import "./RequestTenantUpdateModal.css";

const SECTION_CONFIG = [
  {
    key: "idDocument",
    titleKey: "idDocument",
    titleFallback: "ID document",
    subtitleKey: "dniNieOrPassport",
    subtitleFallback: "DNI, NIE or Passport",
    icon: "/images/id-document.svg",
    value: "ID document",
  },
  {
    key: "financial",
    titleKey: "financialInformation",
    titleFallback: "Financial information",
    subtitleKey: "incomeAndStatements",
    subtitleFallback: "Income and statements",
    icon: "/images/financial.svg",
    value: "Financial information",
  },
  {
    key: "documents",
    titleKey: "documents",
    titleFallback: "Documents",
    subtitleKey: "specificFiles",
    subtitleFallback: "Specific files",
    icon: "/images/document.svg",
    value: "Documents",
  },
  {
    key: "guarantor",
    titleKey: "guarantor",
    titleFallback: "Guarantor",
    subtitleKey: "missingOrIncomplete",
    subtitleFallback: "Missing or incomplete",
    icon: "/images/id-document-1.svg",
    value: "Guarantor",
  },
];

const RequestTenantUpdateModal = ({
  isOpen,
  onClose,
  tenants = [],
  isSending = false,
  onSend,
}) => {
  const { _t } = useTranslator();
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [isDocumentSelected, setIsDocumentSelected] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (isOpen && tenants.length) {
      setSelectedTenantIds([tenants[0]._id]);
      setSelectedSections([]);
      setIsDocumentSelected(false);
      setSelectedDocuments([]);
      setNote("");
    } else if (isOpen) {
      setSelectedTenantIds([]);
      setSelectedSections([]);
      setIsDocumentSelected(false);
      setSelectedDocuments([]);
      setNote("");
    }
  }, [isOpen, tenants]);

  if (!isOpen) return null;

  const toggleTenant = (id) => {
    setSelectedTenantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSection = (section) => {
    if (section.key === "documents") {
      const next = !isDocumentSelected;
      setIsDocumentSelected(next);
      if (!next) setSelectedDocuments([]);
      return;
    }
    setSelectedSections((prev) =>
      prev.includes(section.value)
        ? prev.filter((v) => v !== section.value)
        : [...prev, section.value]
    );
  };

  const toggleDocument = (docName) => {
    setSelectedDocuments((prev) =>
      prev.includes(docName)
        ? prev.filter((d) => d !== docName)
        : [...prev, docName]
    );
  };

  const hasSelection =
    selectedSections.length > 0 ||
    (isDocumentSelected && selectedDocuments.length > 0);
  const canSend = selectedTenantIds.length > 0 && hasSelection && !isSending;

  const handleSend = async () => {
    if (!canSend) return;
    const finalSelectedItems = [
      ...selectedSections,
      ...(isDocumentSelected ? selectedDocuments : []),
    ];
    await onSend?.({
      tenantIds: selectedTenantIds,
      selectedItems: finalSelectedItems,
      note,
    });
  };

  const selectedTenants = tenants.filter((t) =>
    selectedTenantIds.includes(t._id)
  );
  const primaryTenant = selectedTenants[0] || null;

  // Build summary like "Noelia: ID document + Documents"
  const activeLabels = [
    ...selectedSections,
    ...(isDocumentSelected ? ["Documents"] : []),
  ];
  const summaryText = selectedTenants.length
    ? selectedTenants
        .map(
          (t) =>
            `${t.name}: ${activeLabels.join(" + ") || _t("nothingSelected", "Nothing selected")}`
        )
        .join(", ")
    : "";

  const isManualDoc = (doc) => {
    const lower = doc.toLowerCase();
    return (
      lower.includes("working life") ||
      lower.includes("social security") ||
      lower.includes("manual")
    );
  };

  return (
    <div className="request-update-modal-overlay" onClick={onClose}>
      <div
        className="request-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-update-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          iconStart={<Icon name="close" />}
          className="request-update-modal-close"
          onClick={onClose}
          aria-label="Close"
        />

        <div className="request-update-modal-header">
          <h2 id="request-update-modal-title">
            {_t("sendFeedbackToTheTenants", "Send Feedback To The Tenants")}
          </h2>
          <p>
            {_t(
              "sendFeedbackToTheTenantDescription",
              "Let each tenant know what's missing, ask questions or share any feedback on their application."
            )}
          </p>
        </div>

        <div className="request-update-modal-body">
          {tenants.length > 0 && (
            <div className="request-update-tenant-chips">
              {tenants.map((t) => {
                const isSelected = selectedTenantIds.includes(t._id);
                const count = t.missingDocs?.length || 0;
                return (
                  <RowButton
                    key={t._id}
                    type="button"
                    onClick={() => toggleTenant(t._id)}
                    className={`request-update-chip ${isSelected ? "active" : ""}`}
                  >
                    <span className="request-update-chip-avatar">
                      {t.initials || t.name?.slice(0, 2).toUpperCase() || "NA"}
                    </span>
                    <span className="request-update-chip-info">
                      <strong>{t.name}</strong>
                      <span>
                        {count ? `${count} items` : _t("empty", "empty")}
                      </span>
                    </span>
                  </RowButton>
                );
              })}
            </div>
          )}

          <div className="request-update-flag-grid">
            {SECTION_CONFIG.map((section) => {
              const isActive =
                section.key === "documents"
                  ? isDocumentSelected
                  : selectedSections.includes(section.value);
              return (
                <div
                  key={section.key}
                  onClick={() => toggleSection(section)}
                  className={`flag-card cursor-pointer d-flex align-items-center ${isActive ? "active" : ""}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection(section);
                    }
                  }}
                >
                  <img src={section.icon} alt="" />
                  <div className="flag-card-text">
                    <h5>{_t(section.titleKey, section.titleFallback)}</h5>
                    <p>{_t(section.subtitleKey, section.subtitleFallback)}</p>
                  </div>
                  <span
                    className={`flag-card-check ${isActive ? "checked" : ""}`}
                  >
                    {isActive ? "✓" : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {isDocumentSelected && primaryTenant && (
            <div className="request-update-docs-section">
              <h4 className="request-update-docs-title">
                {_t(
                  "whichDocumentsNeeded",
                  "Which documents do you need from {{name}}?",
                  undefined,
                  { name: primaryTenant.name }
                )}
              </h4>
              {primaryTenant.missingDocs?.length ? (
                <div className="request-update-docs-list-box">
                  {primaryTenant.missingDocs.map((doc) => {
                    const checked = selectedDocuments.includes(doc);
                    const manual = isManualDoc(doc);
                    return (
                      <Checkbox
                        key={`${primaryTenant._id}-${doc}`}
                        checked={checked}
                        onChange={() => toggleDocument(doc)}
                        label={
                          <span className="request-update-doc-row">
                            <span className="request-update-doc-name">
                              {doc}
                            </span>
                            <span
                              className={`request-update-doc-badge ${manual ? "manual" : "missing"}`}
                            >
                              {!manual && <span className="badge-dot" />}
                              {manual ? "Manual" : _t("missing", "Missing")}
                            </span>
                          </span>
                        }
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="request-update-docs-empty">
                  {_t(
                    "noMissingDocumentsForSelected",
                    "No missing documents for selected tenants."
                  )}
                </p>
              )}
              {selectedTenants.length > 1 && (
                <p className="request-update-docs-multi-hint">
                  {_t(
                    "documentsApplyToAll",
                    "Selected documents will be requested from all {{count}} selected tenants.",
                    undefined,
                    { count: selectedTenants.length }
                  )}
                </p>
              )}
            </div>
          )}

          <TextArea
            id="request-update-note"
            label={_t(
              "personalNoteToInclude",
              "Personal note to include in the email"
            )}
            placeholder={_t(
              "requestUpdateNotePlaceholder",
              "E.g. I need these documents before the 30th to move forward with your application."
            )}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />

          {hasSelection ? (
            <p className="request-update-summary">{summaryText}</p>
          ) : (
            <p className="request-update-hint">
              {_t(
                "nothingSelectedNoEmails",
                "Nothing selected yet — no emails will be sent."
              )}
            </p>
          )}
        </div>

        <div className="request-update-modal-footer">
          <Button
            variant="primary"
            size="lg"
            disabled={!canSend}
            onClick={handleSend}
          >
            {isSending
              ? `${_t("sending", "Sending")}...`
              : _t(
                  "sendToNTenants_one",
                  "Send to {{count}} tenant",
                  undefined,
                  { count: selectedTenantIds.length }
                ) + (selectedTenantIds.length === 1 ? "" : "s")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequestTenantUpdateModal;
